import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'

interface ClientInfo {
  id: number
  name: string
  brand: string
  industry?: string
  meta_account_id: string
}

interface UserDetail {
  id: number
  email: string
  role: string
  created_at: string
  clients: ClientInfo[]
}

interface UserList {
  id: number
  email: string
  role: string
  client_count: number
  created_at: string
}

export default function UserManagement() {
  const { user, loading: authLoading } = useAuth()
  const [users, setUsers] = useState<UserList[]>([])
  const [availableClients, setAvailableClients] = useState<ClientInfo[]>([])
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'advertiser',
    client_ids: [] as number[]
  })

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: 'var(--spacing-8)'
      }}>
        <h2 style={{ marginBottom: 'var(--spacing-4)' }}>Access Denied</h2>
        <p style={{ color: 'var(--color-secondaryLabel)' }}>
          Only administrators can access user management.
        </p>
      </div>
    )
  }

  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to complete
    }
    
    if (user) {
      fetchUsers()
      fetchAvailableClients()
    }
  }, [user, authLoading, searchTerm, roleFilter])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (roleFilter) params.append('role', roleFilter)
      
      const response = await api.get(`/users/?${params.toString()}`)
      setUsers(response.data.users)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableClients = async () => {
    try {
      const response = await api.get('/clients/')
      setAvailableClients(response.data)
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const fetchUserDetail = async (userId: number) => {
    try {
      const response = await api.get(`/users/${userId}`)
      setSelectedUser(response.data)
      setFormData({
        email: response.data.email,
        password: '',
        role: response.data.role,
        client_ids: response.data.clients.map((c: ClientInfo) => c.id)
      })
    } catch (error) {
      console.error('Error fetching user detail:', error)
      toast.error('Failed to fetch user details')
    }
  }

  const handleCreateUser = async () => {
    try {
      await api.post('/users/', formData)
      toast.success('User created successfully')
      setShowCreateModal(false)
      resetForm()
      fetchUsers()
    } catch (error: any) {
      console.error('Error creating user:', error)
      toast.error(error.response?.data?.detail || 'Failed to create user')
    }
  }

  const handleUpdateUser = async () => {
    if (!selectedUser) return
    
    try {
      const updateData: any = {
        role: formData.role,
        client_ids: formData.client_ids
      }
      
      if (formData.email !== selectedUser.email) {
        updateData.email = formData.email
      }
      
      if (formData.password) {
        updateData.password = formData.password
      }

      await api.put(`/users/${selectedUser.id}`, updateData)
      toast.success('User updated successfully')
      setShowEditModal(false)
      setSelectedUser(null)
      resetForm()
      fetchUsers()
    } catch (error: any) {
      console.error('Error updating user:', error)
      toast.error(error.response?.data?.detail || 'Failed to update user')
    }
  }

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return
    
    try {
      await api.delete(`/users/${userId}`)
      toast.success('User deleted successfully')
      fetchUsers()
    } catch (error: any) {
      console.error('Error deleting user:', error)
      toast.error(error.response?.data?.detail || 'Failed to delete user')
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      role: 'advertiser',
      client_ids: []
    })
  }

  const openCreateModal = () => {
    resetForm()
    setShowCreateModal(true)
  }

  const openEditModal = (userId: number) => {
    fetchUserDetail(userId)
    setShowEditModal(true)
  }

  return (
    <div style={{
      padding: 'var(--spacing-6)',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--spacing-6)'
      }}>
        <h1 style={{
          margin: 0,
          color: 'var(--color-label)',
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-bold)'
        }}>
          User Management
        </h1>
        <button
          onClick={openCreateModal}
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            backgroundColor: 'var(--color-blue)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)'
          }}
        >
          Create User
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 'var(--spacing-4)',
        marginBottom: 'var(--spacing-6)',
        padding: 'var(--spacing-4)',
        backgroundColor: 'var(--color-card)',
        borderRadius: '12px',
        border: '1px solid var(--color-border)'
      }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: 'var(--spacing-3)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-card)'
          }}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{
            padding: 'var(--spacing-3)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-card)',
            minWidth: '150px'
          }}
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="advertiser">Advertiser</option>
        </select>
      </div>

      {/* Users Table */}
      <div style={{
        backgroundColor: 'var(--color-card)',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
              <th style={{
                padding: 'var(--spacing-4)',
                textAlign: 'left',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-label)'
              }}>Email</th>
              <th style={{
                padding: 'var(--spacing-4)',
                textAlign: 'left',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-label)'
              }}>Role</th>
              <th style={{
                padding: 'var(--spacing-4)',
                textAlign: 'left',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-label)'
              }}>Clients</th>
              <th style={{
                padding: 'var(--spacing-4)',
                textAlign: 'left',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-label)'
              }}>Created</th>
              <th style={{
                padding: 'var(--spacing-4)',
                textAlign: 'left',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-label)'
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{
                  padding: 'var(--spacing-8)',
                  textAlign: 'center',
                  color: 'var(--color-secondaryLabel)'
                }}>
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} style={{
                  padding: 'var(--spacing-8)',
                  textAlign: 'center',
                  color: 'var(--color-secondaryLabel)'
                }}>
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} style={{
                  borderTop: '1px solid var(--color-border)'
                }}>
                  <td style={{
                    padding: 'var(--spacing-4)',
                    color: 'var(--color-label)'
                  }}>
                    {user.email}
                  </td>
                  <td style={{
                    padding: 'var(--spacing-4)',
                    color: 'var(--color-label)'
                  }}>
                    <span style={{
                      padding: 'var(--spacing-1) var(--spacing-2)',
                      borderRadius: '4px',
                      fontSize: 'var(--font-size-sm)',
                      backgroundColor: user.role === 'admin' ? 'var(--color-red-100)' : 'var(--color-blue-100)',
                      color: user.role === 'admin' ? 'var(--color-red-800)' : 'var(--color-blue-800)'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{
                    padding: 'var(--spacing-4)',
                    color: 'var(--color-label)'
                  }}>
                    {user.client_count} client{user.client_count !== 1 ? 's' : ''}
                  </td>
                  <td style={{
                    padding: 'var(--spacing-4)',
                    color: 'var(--color-secondaryLabel)',
                    fontSize: 'var(--font-size-sm)'
                  }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td style={{
                    padding: 'var(--spacing-4)'
                  }}>
                    <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                      <button
                        onClick={() => openEditModal(user.id)}
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          backgroundColor: 'var(--color-blue)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)'
                        }}
                      >
                        Edit
                      </button>
                      {user.id !== user?.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            backgroundColor: 'var(--color-red)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)'
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--color-modalOverlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-card)',
            borderRadius: '12px',
            padding: 'var(--spacing-6)',
            width: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{
              margin: '0 0 var(--spacing-6) 0',
              color: 'var(--color-label)'
            }}>
              Create New User
            </h2>
            
            <UserForm
              formData={formData}
              setFormData={setFormData}
              availableClients={availableClients}
              onSubmit={handleCreateUser}
              onCancel={() => setShowCreateModal(false)}
              isCreate={true}
            />
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--color-modalOverlay)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-card)',
            borderRadius: '12px',
            padding: 'var(--spacing-6)',
            width: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{
              margin: '0 0 var(--spacing-6) 0',
              color: 'var(--color-label)'
            }}>
              Edit User: {selectedUser.email}
            </h2>
            
            <UserForm
              formData={formData}
              setFormData={setFormData}
              availableClients={availableClients}
              onSubmit={handleUpdateUser}
              onCancel={() => {
                setShowEditModal(false)
                setSelectedUser(null)
              }}
              isCreate={false}
            />
          </div>
        </div>
      )}
    </div>
  )
}

interface UserFormProps {
  formData: {
    email: string
    password: string
    role: string
    client_ids: number[]
  }
  setFormData: React.Dispatch<React.SetStateAction<{
    email: string
    password: string
    role: string
    client_ids: number[]
  }>>
  availableClients: ClientInfo[]
  onSubmit: () => void
  onCancel: () => void
  isCreate: boolean
}

function UserForm({ formData, setFormData, availableClients, onSubmit, onCancel, isCreate }: UserFormProps) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-label)'
        }}>
          Email *
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          required
          style={{
            width: '100%',
            padding: 'var(--spacing-3)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-card)'
          }}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-label)'
        }}>
          Password {!isCreate && '(leave blank to keep current)'}
        </label>
        <input
          type="password"
          value={formData.password}
          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
          required={isCreate}
          style={{
            width: '100%',
            padding: 'var(--spacing-3)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-card)'
          }}
        />
      </div>

      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-label)'
        }}>
          Role *
        </label>
        <select
          value={formData.role}
          onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
          style={{
            width: '100%',
            padding: 'var(--spacing-3)',
            borderRadius: '8px',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-card)'
          }}
        >
          <option value="advertiser">Advertiser</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--spacing-2)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-label)'
        }}>
          Client Connections
        </label>
        <div style={{
          maxHeight: '200px',
          overflow: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: 'var(--spacing-2)'
        }}>
          {availableClients.map((client) => (
            <label key={client.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: 'var(--spacing-2)',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={formData.client_ids.includes(client.id)}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    client_ids: e.target.checked
                      ? [...prev.client_ids, client.id]
                      : prev.client_ids.filter(id => id !== client.id)
                  }))
                }}
                style={{ marginRight: 'var(--spacing-2)' }}
              />
              <div>
                <div style={{ fontWeight: 'var(--font-weight-medium)' }}>
                  {client.brand}
                </div>
                <div style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-secondaryLabel)'
                }}>
                  {client.name} • {client.industry}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: 'var(--spacing-3)',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={onCancel}
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            backgroundColor: 'var(--color-gray-200)',
            color: 'var(--color-label)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSubmit}
          style={{
            padding: 'var(--spacing-3) var(--spacing-6)',
            backgroundColor: 'var(--color-blue)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          {isCreate ? 'Create User' : 'Update User'}
        </button>
      </div>
    </div>
  )
} 